import NoData from '@rio-cloud/rio-uikit/components/noData/NoData';
import isNil from 'lodash/fp/isNil';
import isUndefined from 'lodash/fp/isUndefined';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { DEFAULT_ROUTE, SUMMARY_ROUTE } from '../../common/constants';
import type { Coordinates } from '../../common/types';
import { coordinatesToCoords } from '../../common/utils';
import { VehicleStatusIndicator } from '../../components/VehicleStatusIndicator/VehicleStatusIndicator';
import { useAppSelector } from '../../configuration/setup/hooks';
import { useRouteWidgetContext } from '../../context/RouteWidgetContext';
import { useGetAssetSubscriptionStateQuery } from '../../store/facade/facadeApiV3';
import { selectIsVehicleSwitchPending, selectVehicleProfile } from '../../store/vehicleProfile/vehicleProfileSelectors';
import { selectSelectedAssetId } from '../../store/widget/widgetSelectors';
import { VehicleAutoSuggest } from './VehicleAutoSuggest';
import { VehicleIcon } from './VehicleIcon';
import { WarningMessage } from './WarningMessage';

export const VehicleSelection = () => {
    const intl = useIntl();
    const widgetContext = useRouteWidgetContext();

    const { pathname } = useLocation();
    const isInSettingsPage = pathname === DEFAULT_ROUTE;
    const isInSummaryPage = pathname === SUMMARY_ROUTE;

    const selectedAssetId = useAppSelector(selectSelectedAssetId);
    const assetLocked = useAppSelector(selectVehicleProfile).assetLocked;
    const isBev = assetLocked?.details?.details_bev ?? false;
    const assetName = assetLocked?.name;
    const assetType = assetLocked?.vehicleType;
    const assetPosition = assetLocked?.position && coordinatesToCoords(assetLocked?.position as Coordinates);

    const vehiclesLoading = useAppSelector(selectIsVehicleSwitchPending);

    const { isLoading: bookingLoading, isFetching: bookingFetching } = useGetAssetSubscriptionStateQuery(
        { id: assetLocked?.asset_id ?? '' },
        { skip: isNil(assetLocked) }
    );

    const hasAsset = !isUndefined(assetLocked || widgetContext.assetSelected);
    const isBookingPending = !isNil(assetLocked) && (bookingLoading || bookingFetching);
    const isLoadingData = vehiclesLoading || isBookingPending;
    const displayLackPosition = !isLoadingData && !isNil(assetLocked) && isNil(assetPosition);
    const hasFeedback = hasAsset && displayLackPosition;

    return (
        <div
            className={`flex-1 justify-content-between align-items-center margin-top-5 form-group ${
                hasFeedback ? 'has-feedback has-warning' : 'margin-0'
            }`}
        >
            {isInSettingsPage && <VehicleAutoSuggest />}
            {!isLoadingData &&
                (hasAsset ? (
                    <div
                        className='display-flex justify-content-between align-items-center'
                        data-testid='selected-asset-card'
                    >
                        <div className='display-flex' key={selectedAssetId} data-testid='vehicle-selected'>
                            {isInSummaryPage && <VehicleIcon isBev={isBev} assetType={assetType} />}
                            {isInSummaryPage && (
                                <div className='display-flex flex-column'>
                                    <span className='text-left' data-testid='asset-name'>
                                        {assetName}
                                        <VehicleStatusIndicator />
                                    </span>

                                    {displayLackPosition && (
                                        <WarningMessage messageId='intl-msg:smartRoute.vehicle.noPositionData' />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    isInSummaryPage && (
                        <div
                            className='display-flex justify-content-between align-items-center'
                            data-testid='unselected-asset-card'
                        >
                            <NoData
                                text={intl.formatMessage({
                                    id: 'intl-msg:smartRoute.vehicle.noVehicleSelected',
                                })}
                                className='text-color-dark text-transform-none white-space-normal'
                                tooltip={intl.formatMessage({
                                    id: 'intl-msg:smartRoute.vehicle.noVehicleSelected.tooltip',
                                })}
                                tooltipPlacement='bottom'
                                data-testid='no-vehicle-selected'
                            />
                        </div>
                    )
                ))}
            {isInSettingsPage && !isLoadingData && hasFeedback && (
                <div>
                    <span className='form-control-feedback'>
                        <span className='rioglyph rioglyph-warning-sign margin-right-5' />
                    </span>
                    {displayLackPosition && (
                        <span className='help-block'>
                            <FormattedMessage id='intl-msg:smartRoute.vehicle.noPositionData' />
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};
